/**
 * The option lists both standards editors show (standards selects spec §3, §6): the web
 * panel's Standards page and the plugin's "This deck". One source, so the two surfaces
 * offer the same presets under the same words. Values are the contract ids (a month as its
 * number in text); "Not set" is a sentinel the editors map to the field's absence.
 */
import { currencyExample } from "./describe.js";
import {
  CALENDAR_YEAR_FORMATS,
  CURRENCIES,
  CURRENCY_STYLES,
  FISCAL_STYLES,
  LANGUAGE_VARIANTS,
  MONTH_NAMES,
  calendarYearFormatSchema,
  currencyFormatSchema,
  fiscalYearFormatSchema,
  languageVariantSchema,
  type CurrencyCode,
  type CurrencyFormat,
  type FiscalYearFormat,
} from "./formats.js";
import type { FirmStandards } from "./standards.js";

export type SelectOption = { value: string; label: string };

/** "Not set": no standard for this field (the model uses its own judgment). */
export const NOT_SET_OPTION: SelectOption = { value: "not-set", label: "Not set" };

export const CURRENCY_OPTIONS: readonly SelectOption[] = CURRENCIES.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));

/** "Written as": each option is its example in the chosen currency ("$1.2M"; Dan 2026-10-02,
 * no explanation before it). With no currency chosen the select is disabled, so the plain
 * names stand in. The model still gets the explanation (describeStandard). */
export function currencyStyleOptions(currency: CurrencyCode | null): SelectOption[] {
  return CURRENCY_STYLES.map((s) => ({
    value: s.id,
    label: currency ? currencyExample(currency, s.id) : s.label,
  }));
}

/** Each format shows as its example alone ("Mar 2025"), as do the fiscal styles ("FY25"). */
export const CALENDAR_YEAR_OPTIONS: readonly SelectOption[] = CALENDAR_YEAR_FORMATS.map((p) => ({ value: p.id, label: p.example }));

export const FISCAL_STYLE_OPTIONS: readonly SelectOption[] = FISCAL_STYLES.map((p) => ({ value: p.id, label: p.example }));

export const MONTH_OPTIONS: readonly SelectOption[] = MONTH_NAMES.map((name, i) => ({ value: String(i + 1), label: name }));

export const LANGUAGE_OPTIONS: readonly SelectOption[] = LANGUAGE_VARIANTS.map((v) => ({ value: v.id, label: v.label }));

/** The four format fields as the editors hold them: one string per select, "" = not set
 * (a month as "1".."12"). */
export type FormatSelects = {
  languageVariant: string;
  currency: string;
  currencyStyle: string;
  calendarYearFormat: string;
  fiscalStyle: string;
  fiscalEndMonth: string;
};

export function formatSelectsOf(s: FirmStandards | null | undefined): FormatSelects {
  return {
    languageVariant: s?.languageVariant ?? "",
    currency: s?.currencyFormat?.currency ?? "",
    currencyStyle: s?.currencyFormat?.style ?? "",
    calendarYearFormat: s?.calendarYearFormat ?? "",
    fiscalStyle: s?.fiscalYearFormat?.style ?? "",
    fiscalEndMonth: s?.fiscalYearFormat?.endMonth ? String(s.fiscalYearFormat.endMonth) : "",
  };
}

type FormatFields = Pick<FirmStandards, "languageVariant" | "currencyFormat" | "calendarYearFormat" | "fiscalYearFormat">;

/** The selects as contract values: a field the schema refuses (a currency without its
 * style, an end month without a format) is left out. */
export function formatsFromSelects(sel: FormatSelects): FormatFields {
  // The casts are the contract types themselves: the plugin isn't strict, so there zod
  // infers every key optional (contract/parity.ts).
  const out: FormatFields = {};
  const language = languageVariantSchema.safeParse(sel.languageVariant);
  if (language.success) out.languageVariant = language.data;
  const currency = currencyFormatSchema.safeParse({ currency: sel.currency, style: sel.currencyStyle });
  if (currency.success) out.currencyFormat = currency.data as CurrencyFormat;
  const calendar = calendarYearFormatSchema.safeParse(sel.calendarYearFormat);
  if (calendar.success) out.calendarYearFormat = calendar.data;
  const fiscal = fiscalYearFormatSchema.safeParse(
    sel.fiscalEndMonth ? { style: sel.fiscalStyle, endMonth: Number(sel.fiscalEndMonth) } : { style: sel.fiscalStyle },
  );
  if (fiscal.success) out.fiscalYearFormat = fiscal.data as FiscalYearFormat;
  return out;
}

/** A currency picked without how it's written: the editors show an error and won't save. */
export function currencyIncomplete(sel: Pick<FormatSelects, "currency" | "currencyStyle">): boolean {
  return (sel.currency !== "") !== (sel.currencyStyle !== "");
}

/** The copy for `currencyIncomplete`. */
export const CURRENCY_INCOMPLETE_COPY = "Choose how amounts are written.";
